import React, { useContext } from 'react'
import useReservationUpdates from 'src/features/myOffers/useReservationUpdates'
import { FormattedMessage, Link, useIntl } from 'gatsby-plugin-intl'
import routes from 'src/utils/routes'
import Pagination from 'src/components/Pagination'
import {
  BottomRow,
  Container,
  Counter,
  Ellipsis,
  EmptyOffers,
  RefreshIcon
} from 'src/features/stocks/components/atoms'
import LoadingList from 'src/components/atoms/LoadingList'
import Shade from 'src/components/Shade'
import Checkbox from 'src/components/atoms/CheckboxInput'
import { BOOLEAN, EXCHANGE_API_URL_PREFIXES } from 'src/ducks/consts'
import { AddOfferButton } from 'src/components/atoms/Buttons'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'

import PermissionsContext, {
  hasPermissionTo,
  PERMISSIONS
} from 'src/utils/permissions'
import { canAddOffer } from 'src/features/offers/utils/exchangePermissions'
import { connectExportOngoing } from 'src/features/myOffers/duck/connectors'
import { HEADERS_ONGOING } from 'src/features/myOffers/duck/consts'
import {
  ActionGroup,
  CounterGroup,
  HeaderItem,
  Selected,
  TableHeader
} from 'src/features/myOffers/components/atoms'
import { isStockLoading } from 'src/utils/stocks'
import DeleteMultiple from 'src/features/myOffers/components/DeleteMultiple'
import Offer from 'src/features/myOffers/components/export/OfferRowOngoing'
import { Icon } from 'src/features/account/components/atoms'
import ArrowIcon from 'src/assets/icons/arrow-down.svg'
import { ListTopRow } from 'src/features/contracts/components/MyOffersOngoing'
import {
  SearchFilter,
  SelectWrapper
} from 'src/components/filters/components/atoms'
import { REFERENCE_NUMBER_PREFIX } from 'src/features/stocks/ducks/consts'

const OffersList = ({
  ongoingOffers,
  myOffers: offers,
  pagination,
  filters,
  setFilter,
  selectedOffers,
  deleteMultipleOffers,
  selectAllOffers,
  clearSelectedOffers,
  hidePagination = false,
  getOngoingOffers
}) => {
  useReservationUpdates('exportOngoing')
  const headers = HEADERS_ONGOING.MY_EXPORT
  const intl = useIntl()
  const permissions = useContext(PermissionsContext)
  const hasPermissionToAddOffer = canAddOffer(
    EXCHANGE_API_URL_PREFIXES.EXPORT,
    permissions
  )
  const hasPermissionToDeleteOwnOffer = hasPermissionTo(
    PERMISSIONS.OFFER_DELETE_OWN,
    permissions
  )
  const allOffersSelected = selectedOffers.length === offers.length

  const toggleFilter = name => {
    setFilter(name, !filters.onlyMe)
  }

  const handleSelectAll = () =>
    allOffersSelected ? clearSelectedOffers() : selectAllOffers()

  const renderListContents = () => {
    if (isStockLoading(ongoingOffers, 'list')) {
      return (
        <>
          <Shade />
          <LoadingList />
        </>
      )
    }

    if (offers.length === 0) {
      return (
        <EmptyOffers>
          <FormattedMessage id='offers.noOffers' />
        </EmptyOffers>
      )
    }

    if (offers.length > 0) {
      return offers.map(offer => (
        <Offer
          key={offer.id}
          offer={offer}
          isSelected={selectedOffers.includes(offer.id)}
          headers={headers}
          hasPermissionToDeleteOwnOffer={hasPermissionToDeleteOwnOffer}
        />
      ))
    }
  }

  return (
    <>
      <ListTopRow>
        <CounterGroup>
          <Counter>
            <span>{`${pagination.total} `}</span>
            <FormattedMessage
              id='offers.offersFound'
              values={{
                count: pagination.total
              }}
            />
          </Counter>
          <RefreshIcon onClick={getOngoingOffers} />
        </CounterGroup>
        <ActionGroup
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'nowrap'
          }}
        >
          <SelectWrapper
            style={{
              marginRight: '15px',
              flexGrow: 1,
              maxWidth: '300px'
            }}
          >
            <SearchFilter
              name='referenceNumber'
              id='input-search-export-ongoing'
              initValue={REFERENCE_NUMBER_PREFIX.CARGO_EXPORT}
              filterValue={filters.referenceNumber}
              setFilter={setFilter}
              placeholder={intl.formatMessage({ id: 'common.offerNumber' })}
              style={{
                display: 'flex',
                width: '100%'
              }}
            />
          </SelectWrapper>
          <Checkbox
            name='onlyMe'
            value={filters.onlyMe}
            onChange={toggleFilter}
            option={{
              text: intl.formatMessage({ id: 'offers.onlyMe' }),
              value: BOOLEAN.YES
            }}
          />
          {hasPermissionToAddOffer && (
            <Link
              to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.OFFER_FORM_EXPORT}`}
            >
              <AddOfferButton />
            </Link>
          )}
        </ActionGroup>
      </ListTopRow>
      <Container>
        {selectedOffers.length > 0 && hasPermissionToDeleteOwnOffer && (
          <DeleteMultiple
            onClick={deleteMultipleOffers}
            selectedOffersCount={selectedOffers.length}
          />
        )}
        <TableHeader numberOfHeaders={headers.length}>
          {offers.length > 0 ? (
            <Selected
              name='selectAll'
              value={allOffersSelected}
              onChange={handleSelectAll}
              option={{
                text: '',
                value: BOOLEAN.YES
              }}
            />
          ) : (
            <HeaderItem></HeaderItem>
          )}

          {headers &&
            headers.map((header, index) => (
              <HeaderItem sort={false} key={`${header.name} ${index}`}>
                <Ellipsis>
                  <FormattedMessage id={header.name} defaultMessage=' ' />
                  {header.sort && (
                    <Icon>
                      <ArrowIcon />
                    </Icon>
                  )}
                </Ellipsis>
              </HeaderItem>
            ))}
        </TableHeader>
        {renderListContents()}
      </Container>
      {offers.length > 0 && !hidePagination && (
        <BottomRow>
          <Pagination
            itemsPerPage={filters.limit}
            currentPage={pagination.currentPage}
            pagesCount={pagination.lastPage}
            setPage={page => setFilter('page', page)}
            setItemsPerPage={limit => {
              setFilter('limit', limit)
              setFilter('page', 1)
            }}
          />
        </BottomRow>
      )}
    </>
  )
}

export default connectExportOngoing(OffersList)
